import { useEffect, useRef } from 'react'

import * as Sentry from '@sentry/nextjs'

import { VideoDimensionsIProps } from '@/features/video-player'
import { useUpdateCameraMutation } from '@/graphql/generated/hooks'

/*
 * Persists a camera's streaming resolution from a live capture.
 *
 * Writes once per camera on the first valid capture & idempotent.
 * Re-fires when the camera changes (e.g. switching tailgate connections
 * without a full remount). A failed write is retried on a later capture.
 */
export const useEnsureCameraStreamingResolution = (
  cameraId: string | undefined,
  videoDimensions: VideoDimensionsIProps | undefined,
  captureCount: number
) => {
  const persistedCameraIdRef = useRef<string>()

  const [updateCamera] = useUpdateCameraMutation()

  useEffect(() => {
    if (!cameraId) return
    if (persistedCameraIdRef.current === cameraId) return
    // captureCount resets to 0 on each frame reset, so this proves the dims
    // are from this camera, not a stale capture left over from the last one.
    if (captureCount < 1) return
    if (!videoDimensions?.width || !videoDimensions?.height) return

    // Set first so an in-flight write can't double-fire.
    persistedCameraIdRef.current = cameraId
    updateCamera({
      variables: {
        input: {
          id: cameraId,
          streamingResolutionWidth: videoDimensions.width,
          streamingResolutionHeight: videoDimensions.height,
        },
      },
    }).catch((error) => {
      // Let a later capture retry, and surface the failure.
      if (persistedCameraIdRef.current === cameraId) {
        persistedCameraIdRef.current = undefined
      }
      Sentry.captureException(error, {
        tags: {
          feature: 'tailgate_grid',
          action: 'backfill_streaming_resolution',
        },
        extra: {
          cameraId,
          width: videoDimensions.width,
          height: videoDimensions.height,
        },
      })
    })
  }, [cameraId, videoDimensions, captureCount, updateCamera])
}
